import {
  YesNoUnknownOption,
  type ReportFormData,
} from '@/screens/ReportCreation/reportCreation.types';

type YesNoUnknownEnum = {
  yes: string;
  no: string;
  dontKnow: string;
};

interface DiagnosticProcedurePayload<TCategory extends string = string> {
  categoryOfTest?: TCategory;
  extraTestInformation?: string;
  numberOfPositiveTests?: number;
  numberOfNegativeTests?: number;
  numberOfTests?: number;
  sampleTypeCode?: string;
  subCategoryCode?: string;
  familyCode?: string;
}

export const isNonEmptyString = (
  value: string | number | Date | undefined | null,
): value is string => typeof value === 'string' && value.trim().length > 0;

export const resolveSpeciesValue = (
  values: ReportFormData,
): string | undefined => {
  if (isNonEmptyString(values.species)) {
    return values.species.trim();
  }

  return undefined;
};

export const toNumber = (
  value: string | number | undefined,
): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (isNonEmptyString(value)) {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
};

export const toDateString = (
  value: Date | string | undefined,
): string | undefined => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  if (isNonEmptyString(value)) {
    const normalizedValue = value.trim();

    const isoLikeMatch = normalizedValue.match(
      /^(\d{4})[-/](\d{2})[-/](\d{2})$/,
    );
    if (isoLikeMatch) {
      const [, yearValue, monthValue, dayValue] = isoLikeMatch;
      const year = Number(yearValue);
      const month = Number(monthValue);
      const day = Number(dayValue);
      const parsedDate = new Date(Date.UTC(year, month - 1, day));

      if (
        parsedDate.getUTCFullYear() === year &&
        parsedDate.getUTCMonth() + 1 === month &&
        parsedDate.getUTCDate() === day
      ) {
        return parsedDate.toISOString();
      }

      return undefined;
    }

    const dayFirstMatch = normalizedValue.match(
      /^(\d{2})[-/](\d{2})[-/](\d{4})$/,
    );
    if (dayFirstMatch) {
      const [, dayValue, monthValue, yearValue] = dayFirstMatch;
      const day = Number(dayValue);
      const month = Number(monthValue);
      const year = Number(yearValue);
      const parsedDate = new Date(Date.UTC(year, month - 1, day));

      if (
        parsedDate.getUTCFullYear() === year &&
        parsedDate.getUTCMonth() + 1 === month &&
        parsedDate.getUTCDate() === day
      ) {
        return parsedDate.toISOString();
      }

      return undefined;
    }

    const parsedDate = new Date(normalizedValue);
    return Number.isNaN(parsedDate.getTime())
      ? undefined
      : parsedDate.toISOString();
  }

  return undefined;
};

export const toEnumValue = <T extends Record<string, string>>(
  enumValue: T,
  value: string | undefined,
): T[keyof T] | undefined => {
  if (!isNonEmptyString(value)) {
    return undefined;
  }

  const isEnumMember = (candidate: string): candidate is T[keyof T] =>
    Object.values(enumValue).includes(candidate);

  return isEnumMember(value) ? value : undefined;
};

export const mapYesNoUnknown = <T extends YesNoUnknownEnum>(
  enumValue: T,
  value: YesNoUnknownOption | undefined,
): T['yes'] | T['no'] | T['dontKnow'] | undefined => {
  if (value === YesNoUnknownOption.yes) {
    return enumValue.yes;
  }

  if (value === YesNoUnknownOption.no) {
    return enumValue.no;
  }

  if (value === YesNoUnknownOption.iDontKnow) {
    return enumValue.dontKnow;
  }

  return undefined;
};

export const mapKnownCause = (
  value: YesNoUnknownOption | undefined,
): boolean | undefined => {
  if (value === YesNoUnknownOption.yes) {
    return true;
  }

  if (value === YesNoUnknownOption.no) {
    return false;
  }

  return undefined;
};

export const getDefinedValue = <T, K extends string>(
  key: K,
  value: T | undefined,
): Partial<Record<K, T>> =>
  value !== undefined ? ({ [key]: value } as Record<K, T>) : {};

export const buildDiagnosticProcedures = <
  TCategoryEnum extends Record<string, string>,
  TProcedure,
>(
  procedures: ReportFormData['diagnosticProcedures'],
  categoryEnum: TCategoryEnum,
  mapProcedure: (
    payload: DiagnosticProcedurePayload<TCategoryEnum[keyof TCategoryEnum]>,
  ) => TProcedure,
): TProcedure[] | undefined => {
  if (!Array.isArray(procedures)) {
    return undefined;
  }

  const mappedProcedures = procedures
    .map((procedure) => {
      const category = toEnumValue(categoryEnum, procedure.tests?.category);
      const sampleTypeCode = isNonEmptyString(procedure.sampling?.sampleType)
        ? procedure.sampling.sampleType
        : undefined;
      const subCategoryCode = isNonEmptyString(procedure.tests?.subcategory)
        ? procedure.tests.subcategory
        : undefined;
      const familyCode = isNonEmptyString(procedure.tests?.family)
        ? procedure.tests.family
        : undefined;

      const requiresFamily =
        category === categoryEnum.direct || category === categoryEnum.indirect;

      const isComplete =
        Boolean(category && sampleTypeCode && subCategoryCode) &&
        (!requiresFamily || Boolean(familyCode));

      if (!isComplete) {
        return null;
      }

      return mapProcedure({
        categoryOfTest: category,
        sampleTypeCode,
        subCategoryCode,
        familyCode,
        extraTestInformation: isNonEmptyString(procedure.tests?.extraInfo)
          ? procedure.tests.extraInfo
          : undefined,
        numberOfPositiveTests: toNumber(procedure.results?.positiveCount),
        numberOfNegativeTests: toNumber(procedure.results?.negativeCount),
        numberOfTests: toNumber(procedure.results?.totalCount),
      });
    })
    .filter((procedure): procedure is TProcedure => procedure !== null);

  return mappedProcedures.length > 0 ? mappedProcedures : undefined;
};
